import React, { useState, useRef } from 'react';
import { uploadCSV } from '../services/api';

export default function Upload({ onUploadSuccess }) {
  const [dragging, setDragging] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const inputRef = useRef();

  const handleFile = (f) => {
    setError('');
    setResult(null);
    if (!f.name.endsWith('.csv')) {
      setError('Please upload a .csv file');
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      setError('File must be under 10MB');
      return;
    }
    setFile(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files.length) handleFile(e.dataTransfer.files[0]);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setError('');
    setResult(null);
    try {
      const r = await uploadCSV(file);
      setResult(r.data);
      setFile(null);
      onUploadSuccess?.();
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h2 className="text-xl font-bold text-slate-800">Upload Transactions</h2>
        <p className="text-sm text-slate-400 mt-1">
          Upload a CSV file with Date, Description, and Amount columns
        </p>
      </div>

      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        className={`relative cursor-pointer border-2 border-dashed rounded-2xl p-12 text-center transition-all
          ${dragging
            ? 'border-blue-500 bg-blue-50'
            : 'border-slate-200 bg-white hover:border-blue-400 hover:bg-blue-50/30'
          }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".csv"
          className="hidden"
          onChange={(e) => e.target.files.length && handleFile(e.target.files[0])}
        />
        <div className="text-5xl mb-4">
          {file ? '📎' : '📤'}
        </div>
        {file ? (
          <div>
            <p className="text-lg font-semibold text-slate-700">{file.name}</p>
            <p className="text-sm text-slate-400 mt-1">{(file.size / 1024).toFixed(1)} KB</p>
          </div>
        ) : (
          <div>
            <p className="text-lg font-semibold text-slate-600">
              Drop your CSV file here
            </p>
            <p className="text-sm text-slate-400 mt-1">
              or click to browse
            </p>
          </div>
        )}
      </div>

      {/* CSV format guide */}
      <div className="bg-slate-50 rounded-xl p-5 border border-slate-100">
        <h3 className="text-sm font-semibold text-slate-600 mb-3">Expected CSV Format</h3>
        <div className="font-mono text-xs bg-white rounded-lg p-4 border border-slate-200 text-slate-600 overflow-x-auto">
          <div className="text-slate-400">Date,Description,Amount</div>
          <div>2024-01-15,Coffee Shop,-4.50</div>
          <div>2024-01-15,Salary Deposit,3000.00</div>
          <div>2024-01-16,Grocery Store,-85.20</div>
        </div>
        <p className="text-xs text-slate-400 mt-3">
          💡 Positive amounts = income, negative amounts = expenses. Category column is optional.
        </p>
      </div>

      {/* Upload button */}
      {file && (
        <button
          onClick={handleUpload}
          disabled={uploading}
          className="w-full py-3.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-semibold rounded-xl shadow-lg shadow-blue-500/25 transition-all disabled:opacity-50"
        >
          {uploading ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
              Uploading…
            </span>
          ) : (
            `Upload ${file.name}`
          )}
        </button>
      )}

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl">
          ❌ {error}
        </div>
      )}

      {/* Success */}
      {result && (
        <div className="p-6 bg-emerald-50 border border-emerald-200 rounded-xl animate-fade-in-up">
          <div className="flex items-start gap-3">
            <span className="text-2xl">✅</span>
            <div>
              <p className="font-semibold text-emerald-800">{result.message}</p>
              <p className="text-sm text-emerald-600 mt-1">
                {result.transactions_count} transactions imported from <strong>{result.file_name}</strong>
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
